// چیدنِ نتیجه‌های جستجوی شهر.
//
// جدا از geoRepo نوشته شده چون تنها چیزیه که می‌شه بدونِ شبکه سنجید — و
// دقیقاً همون چیزیه که یه بار اشتباه بود (نگاه کن به cityRank.test.ts).
import { matchesTokens, normalize, queryTokens } from './discoverSearch.ts';
import type { AppCity, City } from '../domain/types';

/** بیشتر از این توی یه شیت خونده نمی‌شه؛ ادامه‌ش کارِ تایپ کردنه نه اسکرول. */
export const CITY_PAGE = 60;

/**
 * اول اسمِ دقیقاً برابر، بعد اونی که با عبارت شروع می‌شه، بعد اونی که فقط
 * شاملشه.
 *
 * ورودی از قبل بر اساس جمعیت مرتبه و `sort` پایداره، پس داخلِ هر گروه ترتیبِ
 * جمعیت دست‌نخورده می‌مونه — یعنی از بینِ سه تا «York»ِ آمریکا، پرجمعیت‌ترینش
 * اول می‌آد.
 *
 * بدونِ این، تایپ کردنِ «York» اول «New York City» و «East New York» رو می‌داد
 * و خودِ «York» چهارم می‌شد؛ چون واقعاً پرجمعیت‌ترن. جمعیت می‌گه کدوم شهر
 * مهم‌تره، ولی وقتی کاربر داره تایپ می‌کنه، چیزی که نوشته مهم‌تر از جمعیته.
 */
export function rankCities(list: City[], search: string, limit = CITY_PAGE): City[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return list.slice(0, limit);

  const score = (name: string) => {
    const n = name.toLowerCase();
    return n === needle ? 0 : n.startsWith(needle) ? 1 : 2;
  };
  return list
    .filter((c) => c.name.toLowerCase().includes(needle))
    .sort((a, b) => score(a.name) - score(b.name))
    .slice(0, limit);
}

/**
 * فیلترِ **فهرستِ خودمان** — شهرهای اپ توی انتخابگرِ صفحه‌ی کشف.
 *
 * جدا از `rankCities` است و باید هم باشد، چون دو تا فهرستِ متفاوت‌اند:
 * اون یکی روی ۱۵۳ هزار شهرِ دنیا کار می‌کنه که سرور از قبل بریده و بر اساس
 * جمعیت مرتب کرده؛ این یکی روی چند ده شهریه که یک‌جا توی حافظه‌ست و
 * الفبایی مرتبه (نگاه کن به cityRepo.list).
 *
 * دو تفاوتِ رفتاری:
 *
 *   ۱. **کشور و استان هم می‌گردن، نه فقط اسمِ شهر.** «Canada» باید همه‌ی
 *      شهرهای کانادا رو بیاره — کسی که نمی‌دونه ما کدوم شهرها رو داریم،
 *      از اسمِ کشور شروع می‌کنه نه از اسمِ شهر.
 *   ۲. **نرمال‌سازیِ همون جستجوی کشف** (`normalize`/`queryTokens`): «Montreal»
 *      باید «Montréal» رو پیدا کنه و «تهران» با هر شکلی از «ه» و «ی» نوشته
 *      بشه همون یه شهره.
 *
 * فیلدها با `|` به هم وصل می‌شن نه با فاصله‌ی حذف‌شده: بدونش «Ottawa» و
 * «Ontario» می‌چسبیدن به هم و تایپِ «waon» یه نتیجه‌ی بی‌ربط می‌داد.
 */
export function filterAppCities<T extends AppCity>(list: T[], search: string): T[] {
  const tokens = queryTokens(search);
  if (!tokens.length) return list;

  // یه رشته‌ی به‌هم‌چسبیده از همه‌ی کلمه‌های کوئری، برای رتبه‌بندی: «new york»
  // و «newyork» هر دو باید «New York» رو دقیقاً برابر حساب کنن.
  const needle = tokens.join('');
  const flat = (s?: string | null) => normalize(s ?? '').replace(/ /g, '');

  const score = (c: AppCity) => {
    const name = flat(c.name);
    if (name === needle) return 0;
    if (name.startsWith(needle)) return 1;
    if (name.includes(needle)) return 2;
    return 3;   // فقط از راهِ استان/کشور خونده — ته فهرست
  };

  return list
    .filter((c) => matchesTokens(
      [c.name, c.region, c.countryName, c.countryCode].map(flat).join('|'), tokens,
    ))
    // `sort` پایداره، پس داخلِ هر گروه ترتیبِ الفباییِ ورودی می‌مونه.
    .sort((a, b) => score(a) - score(b));
}
